/**
 * 「让这张图动起来」轻确认层(I2V-PC-1,2026-08-30)—— 海报详情页原地直连图生视频。
 *
 * ═══ 为什么是弹层不是跳页 ═══════════════════════════════════════════════
 * 这条路的全部价值 = 少决策马上出:一张图 + 一句动效 → 单镜 i2v,画幅跟原图,
 * 不编分镜、不进 /ai-video/create(那边结构上「无分镜不成片」,fromPoster 一进去
 * 就锁死 90-150s 的两阶 LLM 编排 —— 正是用户抱怨「一键成片不是让图动起来」的根)。
 * 0901 收敛:图上只留这一颗入口,多镜编排(原「据这张图编一部片」按钮)降级为
 * 本弹层底部的升级文字链 —— 轻的先看见,重的要一步升级,两条路都不丢。
 * payload 逐字对齐军师 make_image_video(ai action_tools ADV-I2V-1):
 * FROM_POSTER + sourceRef{taskId, assetId} + panelScenes:[""](恰好 1 项=单镜心脏)
 * + shotMode:["I2V"] + ratio:"adaptive"(成片画幅跟首帧图走,不裁原图)。
 *
 * ═══ 钱的红线(批 L)═══════════════════════════════════════════════════
 * 两步态:step=edit 改参数看 dryRun 预估;step=confirm 参数冻结、落**正式报价单**,
 * 屏上显示的数 = 单上的数 = 结算按单兑现的数。**前端不签任何 MinIO URL**:
 * 请求不带 firstFrameUrl,后端 VideoService「无帧 + 有 sourceRef → 派发前现签 +
 * 校验资产归属」(与军师同一把闸)。
 *
 * ═══ 口播(0901 补上;v1 曾有意裁)═══════════════════════════════════════
 * v1 裁口播的理由是「TTS 费并不进 video.render 那张单」—— 但现行多镜编排路的
 * 事实口径早已是:voiceover 的 TTS 走渲染票内 record_call(ai video_tts stage),
 * 报价面仍是 video 按秒那张单,backend 按单兑现。单镜直发带口播 = 与编排路
 * **同一张票同一口径**,不产生新的账面违规类别。口播同时烧成字幕(多镜同机制)。
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { api } from "@/shared/api";
import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Textarea } from "@/shared/ui/textarea";
import { VoiceoverCapHint, VoiceoverCutSubmitNotice } from "@/shared/ui/VoiceoverCapHint";
import { cn } from "@/shared/lib/utils";
import { SKU, type QuoteParams } from "@/entities/pricing/api/pricingService";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { useBindingQuote, useQuotePreview } from "@/entities/pricing/model/useQuote";
import { grouped } from "@/entities/scene-card/model/creditRates";
import { errorToast } from "@/shared/lib/apiErrors";

/**
 * FSD:features 互不 import(lint 单向核心),所以不引 video feature 的 videoService,
 * 直接打同一个公开口。字段是 `VideoTaskCreateRequest`(video/api/videoService.ts)的
 * **窄投影** —— 只声明本弹层真发的字段;后端 record @JsonIgnoreProperties 同一契约。
 * ★显式声明不靠对象展开:字段名打错时 payload 锚测试(逐字段断言)会红。
 */
interface AnimateVideoCreateRequest {
  quoteId?: string;
  name: string;
  type: "PROMO";
  source: "FROM_POSTER";
  sourceRef: { fromPosterTaskId: number; fromPosterAssetId: number };
  userInput: string;
  provider: "seedance";
  model: string;
  generationMode: "I2V";
  resolution: "720p";
  ratio: string;
  durationSec: number;
  generateAudio: boolean;
  panelScenes: string[];
  panelMotions: string[];
  shotMode: string[];
  /** 口播(可选,0901):TTS 念出 + 烧成字幕,走多镜同一条 voiceovers 契约(单镜=恰好 1 项)。 */
  voiceovers?: string[];
}

/** 与军师 make_image_video 同引擎同档(ai action_tools._I2V_MODEL,eco 档)。 */
const I2V_MODEL = "doubao-seedance-2-0-fast-260128";
const I2V_RESOLUTION = "720p";
/** seedance 2.0 单段硬区间 4~15s;档位离散成 chips(连续滑块会把报价口打成压测)。 */
const DURATION_CHOICES = [4, 6, 8, 10, 12, 15] as const;
const DURATION_DEFAULT = 10;
/** 与 ai 侧 _I2V_DEFAULT_MOTION 逐字一致 —— 两边默认动效不同会让「同一个功能」出两种片。 */
const DEFAULT_MOTION = "画面缓慢推近,光影与元素轻微浮动";

export interface AnimateImageTrigger {
  taskId: number;
  /** 选中产物的 assetId —— 后端按它现签首帧并校验归属(前端不碰 URL)。 */
  assetId: number;
  /** 选中产物的 seq —— 弹层底部「据这张图编一部片」升级链接带进编排页(BP-03 口径)。 */
  assetSeq?: number;
  /** 缩略图(仅预览,不进请求)。 */
  thumbUrl?: string | null;
}

export function AnimateImageDialog({
  trigger,
  onClose,
}: {
  /** null = 不弹。 */
  trigger: AnimateImageTrigger | null;
  onClose: () => void;
}) {
  const [motion, setMotion] = useState("");
  const [voiceover, setVoiceover] = useState("");
  const [duration, setDuration] = useState<number>(DURATION_DEFAULT);
  const [step, setStep] = useState<"edit" | "confirm">("edit");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // 每次重新打开都从干净状态开始(上一次的参数不该粘住)
  useEffect(() => {
    if (trigger) {
      setMotion("");
      setVoiceover("");
      setDuration(DURATION_DEFAULT);
      setStep("edit");
      setSubmitting(false);
    }
  }, [trigger]);

  const { book: priceBook } = usePriceBook();
  // 规格档按 (model, resolution) 从价目表解析,不硬编码档名 —— 平台改档名时这里跟着走,
  // 解析不到(价目表没到货/档位下架)= 降级态:照旧允许提交,后端软着陆 WARN(口径同海报确认卡)。
  const variantKey = priceBook.variantByModel(SKU.video, I2V_MODEL, I2V_RESOLUTION);
  const quoteParams: QuoteParams | null = useMemo(
    () => (variantKey ? { skuCode: SKU.video, variantKey, qty: duration } : null),
    [variantKey, duration]
  );
  // step=edit:dryRun 预估(只算不落库,时长 chips 随便点)。
  const { credits: previewCredits } = useQuotePreview(step === "edit" ? quoteParams : null);
  // step=confirm:参数已冻结,落正式单 —— 屏上的数从这张单来,quoteId 随建单请求走。
  const quote = useBindingQuote(step === "confirm" ? quoteParams : null, previewCredits);

  if (!trigger) return null;

  const motionFinal = motion.trim() || DEFAULT_MOTION;

  async function submit() {
    if (!trigger) return;
    setSubmitting(true);
    try {
      const req: AnimateVideoCreateRequest = {
        // ★单号一路带进建单请求:结算按单兑现,没有单会静默回退旧成本口径(批 L)。
        //   quoteParams 为 null 的降级态例外:后端软着陆 + WARN + 验收判据兜着。
        quoteId: quote.quoteId ?? undefined,
        name: "图生视频·让图动起来",
        type: "PROMO",
        source: "FROM_POSTER",
        // 稳定 ref:后端派发前按它现签首帧 + 校验资产属本租户(ADV-I2V-1 同一把闸)。
        sourceRef: {
          fromPosterTaskId: trigger.taskId,
          fromPosterAssetId: trigger.assetId,
        },
        userInput: motionFinal,
        provider: "seedance",
        model: I2V_MODEL,
        generationMode: "I2V",
        resolution: I2V_RESOLUTION,
        ratio: "adaptive", // 带首帧时成片画幅跟图走,整图不裁
        durationSec: duration,
        generateAudio: true,
        // ★单镜直发的心脏:恰好 1 项的 panel 数组 + I2V —— 多一项就回到多镜编排。
        panelScenes: [""],
        panelMotions: [motionFinal.slice(0, 36)],
        shotMode: ["I2V"],
        // 口播可选(0901):有词才发,恰好 1 项与镜数对齐;TTS+烧字幕与多镜同机制同票
        ...(voiceover.trim() ? { voiceovers: [voiceover.trim()] } : {}),
      };
      const { taskId } = await api.post<{ taskId: number }>("/video/tasks", req);
      toast({
        title: "动效视频已提交",
        description: `任务 ${taskId} 生成中(约 1-3 分钟)—— 到「视频 · 作品」页看进度,好了从那里下载。`,
      });
      onClose();
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "提交失败" }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[16px]">让这张图动起来</DialogTitle>
          <DialogDescription className="text-[12.5px]">
            按这张图原样出一条短动效视频 —— 单镜、画幅跟图走、不重新编分镜。
          </DialogDescription>
        </DialogHeader>

        {trigger.thumbUrl && (
          <div className="flex justify-center">
            <img
              src={trigger.thumbUrl}
              alt="要动起来的图"
              className="max-h-36 rounded-md border border-border/60 object-contain"
            />
          </div>
        )}

        {step === "edit" ? (
          <>
            <div>
              <div className="mb-1 text-[12.5px] text-muted-foreground">
                动效(可不填,默认轻微动效)
              </div>
              <Textarea
                value={motion}
                onChange={(e) => setMotion(e.target.value)}
                placeholder={DEFAULT_MOTION}
                rows={2}
                maxLength={80}
                className="text-[13px]"
              />
            </div>
            <div>
              <div className="mb-1 text-[12.5px] text-muted-foreground">
                口播(可不填;念出来并烧成字幕)
              </div>
              <Textarea
                value={voiceover}
                onChange={(e) => setVoiceover(e.target.value)}
                placeholder={`一句话,约每秒 5 字(${duration} 秒 ≈ ${duration * 5} 字内念得完)`}
                rows={2}
                maxLength={90}
                className="text-[13px]"
                data-testid="animate-voiceover"
              />
              <VoiceoverCapHint text={voiceover} durationSec={duration} subject="这条视频" />
            </div>
            <div>
              <div className="mb-1 text-[12.5px] text-muted-foreground">时长</div>
              <div className="flex flex-wrap gap-1.5">
                {DURATION_CHOICES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDuration(d)}
                    className={cn(
                      "rounded-md border px-2.5 py-1 text-[12.5px] transition-colors",
                      duration === d
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border/60 text-muted-foreground hover:border-primary/50"
                    )}
                  >
                    {d} 秒
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 参数冻结回显 —— 确认这一屏改不了参数,要改回上一步(改了价就变,单就作废) */}
            <div className="rounded-lg bg-muted/30 p-3 text-[12.5px] text-muted-foreground">
              动效:{motionFinal} · 时长 {duration} 秒 · 画幅跟原图
              {voiceover.trim() ? ` · 口播:${voiceover.trim()}` : ""}
            </div>
            <VoiceoverCutSubmitNotice text={voiceover} durationSec={duration} />
            {quote.priceChanged && (
              <div className="rounded-lg border border-warning/40 bg-warning/5 p-2.5 text-[12.5px]">
                <b className="text-foreground">价格已更新</b> —— 你打开之后平台调过价,
                这一单按上面这个数结算。
              </div>
            )}
            {quoteParams == null && (
              <div className="rounded-lg bg-muted/30 p-2.5 text-[12.5px] text-muted-foreground">
                这一单暂时报不出价(规格档还没到齐),按实际用量结算。
              </div>
            )}
            {quote.error && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-2.5 text-[12.5px]">
                <b className="text-foreground">报不出价</b> —— {quote.error}
                。这一单先不提交(没有报价单的任务结算时对不上账)。
              </div>
            )}
          </>
        )}

        {/* 编排升级出口(0901 拍板,入口收敛):图上不再并排两颗按钮,多镜编排从这里进。
            ★这行文字链是 fromPoster 编排流现在**唯一的原生入口**(另一处只是历史任务
            「再来一条」的重放)—— 删它 = 把海报转多镜成片的能力顺手删了。 */}
        {step === "edit" && (
          <button
            type="button"
            data-testid="animate-open-orchestrate"
            className="self-start text-left text-[12px] text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
            onClick={() => {
              onClose();
              navigate(
                `/ai-video/create?fromPoster=${trigger.taskId}&assetSeq=${trigger.assetSeq ?? 1}`
              );
            }}
          >
            想要多镜头分镜、按故事编排的完整成片?据这张图编一部片 →
          </button>
        )}

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="font-mono text-[14px] font-bold text-foreground">
            {step === "edit" ? (
              previewCredits != null ? (
                `约 ${grouped(previewCredits)} 积分`
              ) : (
                ""
              )
            ) : quote.loading ? (
              <span className="inline-block h-4 w-20 animate-pulse rounded bg-muted/60" />
            ) : quote.credits != null ? (
              // ★这个数来自正式那张单,不是预估
              `${grouped(quote.credits)} 积分`
            ) : (
              ""
            )}
          </span>
          <span className="flex gap-2">
            {step === "edit" ? (
              <>
                <Button type="button" variant="outline" onClick={onClose}>
                  取消
                </Button>
                <Button type="button" onClick={() => setStep("confirm")}>
                  下一步
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={() => setStep("edit")}
                >
                  返回改
                </Button>
                {/* 拿不到单号不许提交 —— 但只在「本来问得出价」时(降级态放行,口径同海报卡) */}
                <Button
                  type="button"
                  disabled={
                    submitting || (quoteParams != null && (quote.loading || !quote.quoteId))
                  }
                  onClick={submit}
                >
                  {submitting && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                  生成动效视频
                </Button>
              </>
            )}
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
